/*
 * Copyright (c) 2026 Oray Inc. All rights reserved.
 *
 * No Part of this file may be reproduced, stored
 * in a retrieval system, or transmitted, in any form, or by any means,
 * electronic, mechanical, photocopying, recording, or otherwise,
 * without the prior consent of Oray Inc.
 *
 * @Author: huangweiliang<huangweiliang@oray.com>
 * @Date: 2026-01-13
 * @LastEditors: huangweiliang<huangweiliang@oray.com>
 * @LastEditTime: 2026-01-13 18:42:39
 * @FileName: index.ts
 * @Description: []
 */

import { Env, parseUserAgent } from './env'
import { createPlatformHandler } from './strategies'
import { TerminalEnv, IClientWeb, ClientWebConfig } from './types'
import { DEFAULT_CONFIG } from './config'
import { onConfigChange } from '../../global'

type ListenerCallback = (data: any) => void

class ClientWeb implements IClientWeb {
  private listeners: Map<string, ListenerCallback[]> = new Map()
  private handler = createPlatformHandler()
  private currentConfig: ClientWebConfig = {
    ...DEFAULT_CONFIG,
    clientType: Env.clientType,
  }

  constructor() {
    this.initGlobalReceiver()

    // 监听全局配置变更
    onConfigChange((globalConfig) => {
      if (globalConfig.clientWeb) {
        this.config(globalConfig.clientWeb)
      }
    })
  }

  /**
   * 配置模块
   */
  public config(options: ClientWebConfig) {
    const oldBridgeName = this.currentConfig.globalBridgeName

    this.currentConfig = {
      ...this.currentConfig,
      ...options,
      clientTypeConfigs: {
        ...this.currentConfig.clientTypeConfigs,
        ...options.clientTypeConfigs,
      },
    }

    // 如果桥接名称变更，重新初始化接收器
    if (options.globalBridgeName && options.globalBridgeName !== oldBridgeName) {
      this.updateGlobalReceiver(oldBridgeName, options.globalBridgeName)
    }
  }

  /**
   * 初始化全局接收函数，供 Native 调用
   */
  private initGlobalReceiver() {
    if (typeof window === 'undefined') return
    const bridgeName = this.currentConfig.globalBridgeName || 'OrayClientWebBridge'
    this.mountReceiver(bridgeName)
  }

  private updateGlobalReceiver(oldName: string | undefined, newName: string) {
    if (typeof window === 'undefined') return

    // 移除旧的全局变量
    if (oldName && (window as any)[oldName]) {
      try {
        delete (window as any)[oldName]
      } catch (e) {
        ;(window as any)[oldName] = undefined
      }
    }

    this.mountReceiver(newName)
  }

  private mountReceiver(name: string) {
    // SDK 初始化时强制挂载，确保逻辑受控
    ;(window as any)[name] = {
      emit: (action: string, data?: any) => {
        this._trigger(action, data)
      },
      env: Env,
    }
  }

  /**
   * 内部触发器：执行 JS 端注册的回调
   */
  private _trigger(action: string, data: any) {
    let parsedData = data
    try {
      if (typeof data === 'string' && (data.trim().startsWith('{') || data.trim().startsWith('['))) {
        parsedData = JSON.parse(data)
      }
    } catch (e) {
      // ignore
    }

    const callbacks = this.listeners.get(action)
    if (callbacks && callbacks.length > 0) {
      callbacks.forEach((cb) => {
        try {
          cb(parsedData)
        } catch (e) {
          console.error(`[ClientWeb] Error in listener for ${action}:`, e)
        }
      })
    } else {
      if (typeof (window as any).triggerClientEvent === 'function') {
        ;(window as any).triggerClientEvent(action, parsedData)
      }
    }
  }

  /**
   * 发送消息给 Native (Core API)
   * @param action 事件名
   * @param data 数据
   */
  public send(action: string, data: any = {}) {
    if (!action) {
      console.error('[ClientWeb] Action is required')
      return
    }

    // 获取当前客户端类型配置
    const type = this.currentConfig.clientType || 'default'
    const bridgeConfig = this.currentConfig.clientTypeConfigs?.[type] || this.currentConfig.clientTypeConfigs?.['default']

    try {
      this.handler(action, data, bridgeConfig)
    } catch (e) {
      console.error(`[ClientWeb] Send Failed [${action}]:`, e)
    }
  }

  /**
   * 监听 Native 发来的消息
   * @param action 事件名
   * @param callback 回调
   */
  public on(action: string, callback: ListenerCallback) {
    if (typeof callback !== 'function') return

    if (!this.listeners.has(action)) {
      this.listeners.set(action, [])
    }
    this.listeners.get(action)!.push(callback)
  }

  /**
   * 移除监听
   */
  public off(action: string, callback?: ListenerCallback) {
    if (!this.listeners.has(action)) return

    if (!callback) {
      this.listeners.delete(action)
    } else {
      const cbs = this.listeners.get(action)!
      const index = cbs.indexOf(callback)
      if (index > -1) {
        cbs.splice(index, 1)
      }
      if (cbs.length === 0) {
        this.listeners.delete(action)
      }
    }
  }

  /**
   * 获取环境信息
   */
  public getEnv(): TerminalEnv {
    return Env
  }

  /**
   * 获取客户端友好名称
   */
  public getClientName(): string {
    return Env.clientName || 'Unknown'
  }

  /**
   * (测试用) 解析指定的 UA 字符串
   */
  public parseUA(ua: string): TerminalEnv {
    return parseUserAgent(ua)
  }

  /**
   * 版本号比较
   * @param op 操作符 '=', '>=', '>', '<', '<='
   * @param targetVersion 目标版本
   */
  public compareVersion(op: string, targetVersion: string): boolean {
    const v1 = this.toNum(Env.appVersion)
    const v2 = this.toNum(targetVersion)

    switch (op) {
      case '=':
        return v1 === v2
      case '>=':
        return v1 >= v2
      case '>':
        return v1 > v2
      case '<':
        return v1 < v2
      case '<=':
        return v1 <= v2
      default:
        return false
    }
  }

  private toNum(v: string): string {
    if (!v) return '0'
    const parts = v.split('.')
    return parts.map((p) => p.padStart(4, '0')).join('')
  }
}

const clientWeb = new ClientWeb()
export default clientWeb
